/**
 * Actor role → permission action mapping (admin roles only).
 * Kept in a separate file so capabilityIds can import it without creating
 * a circular dependency (permissions.ts imports capabilityIds).
 */

import type { ActorRole } from './roles';

/**
 * All permission action IDs.
 * This is the complete set used for platform-admin and system-admin.
 * When new PermissionActions are added, add them here — workspace-admin
 * inherits everything except WORKSPACE_ADMIN_EXCLUDED_PERMISSIONS automatically.
 */
export const ALL_PERMISSION_ACTIONS: string[] = [
  'items.view', 'items.create', 'items.triage',
  'items.triage.approve', 'items.triage.route', 'items.triage.attach_relationships',
  'items.manage_ownership',
  'items.edit', 'items.delete', 'items.approve_ai_provenance',
  'relationships.create', 'relationships.edit', 'relationships.delete',
  'process.view', 'process.create', 'process.edit', 'process.prioritize', 'process.publish',
  'process.advance_phase', 'process.legitimacy_advance',
  'cycle.pool.add', 'cycle.pool.remove', 'cycle.abort',
  'tracks.create', 'tracks.configure', 'track.set_official_result',
  'session.view', 'session.facilitate', 'session.record', 'session.participate', 'session.vote',
  'session.pool', 'session.pool.add', 'session.pool.remove', 'session.publish',
  'session.run_ranking', 'session.view_results',
  'pools.select', 'pools.lock', 'pools.reopen',
  'workflow.create', 'workflow.edit', 'workflow.delete', 'workflow.archive', 'workflow.set_default',
  'workflow.assign', 'workflow.use', 'workflow.manage_templates',
  'lists.view', 'lists.create', 'lists.edit', 'lists.move_cutline', 'lists.publish',
  'criteria.view', 'criteria.create', 'criteria.edit', 'criteria.delete',
  'criteria.select', 'criteria.weight', 'criteria.lock', 'criteria.reopen',
  'values.propose', 'values.accept', 'values.reject', 'values.override', 'ranking.override',
  'values.define_range', 'values.approve_range', 'values.define_scales', 'values.converge',
  'attributes.define', 'attributes.assign_owner', 'type_lists.edit',
  'catchball.request', 'catchball.propose', 'catchball.return',
  'catchball.accept_provisional', 'catchball.accept', 'catchball.reject',
  'catchball.rollout', 'catchball.aggregate', 'catchball.adjust_path',
  'analytics.view', 'analytics.export',
  'decision_rights.assign', 'decision_rights.delegate', 'decision_rights.configure',
  'task.assign', 'task.self_assign',
  'admin.users', 'admin.groups', 'admin.roles', 'admin.policies', 'admin.settings',
  'admin.grant_workspace', 'admin.grant_tenant',
];

/**
 * Permissions excluded from workspace-admin even though they exist in ALL_PERMISSION_ACTIONS.
 * These are elevation-granting actions reserved for tenant-admin and platform-admin only.
 *
 * - admin.grant_workspace: Can grant workspace-admin to others (tenant-admin level)
 * - admin.grant_tenant: Can grant tenant-admin across a tenant (platform-admin level)
 */
export const WORKSPACE_ADMIN_EXCLUDED_PERMISSIONS: readonly string[] = [
  'admin.grant_workspace',
  'admin.grant_tenant',
] as const;

/** Admin role → list of permission action IDs. Source of truth for role composition. */
export const ACTOR_ROLE_PERMISSIONS: Record<ActorRole, string[]> = {
  // Baseline role automatically held by every workspace member.
  // Intentionally minimal: grants access to participate in sessions and
  // self-assign tasks. Navigation beyond Dashboard and Tasks requires an
  // explicit higher role (workspace-admin, etc.) or a cycle-participant
  // assignment. This prevents cycle participants from seeing the full nav.
  'workspace-member': [
    'session.participate',
    'task.self_assign',
  ],

  'platform-admin': [...ALL_PERMISSION_ACTIONS],
  'system-admin': [...ALL_PERMISSION_ACTIONS],

  'tenant-admin': [
    'items.view', 'items.create', 'items.triage',
    'items.triage.approve', 'items.triage.route', 'items.triage.attach_relationships',
    'items.manage_ownership',
    'items.edit', 'items.delete', 'items.approve_ai_provenance',
    'relationships.create', 'relationships.edit', 'relationships.delete',
    'process.view', 'process.create', 'process.edit', 'process.prioritize', 'process.publish',
    'process.advance_phase', 'process.legitimacy_advance',
    'cycle.pool.add', 'cycle.pool.remove', 'cycle.abort',
    'tracks.create', 'tracks.configure', 'track.set_official_result',
    'workflow.create', 'workflow.edit', 'workflow.archive', 'workflow.set_default',
    'workflow.assign', 'workflow.use', 'workflow.manage_templates',
    'session.view', 'session.facilitate', 'session.record', 'session.participate', 'session.vote', 'session.publish',
    'session.pool.add', 'session.pool.remove',
    'session.run_ranking', 'session.view_results',
    'pools.select', 'pools.lock', 'pools.reopen',
    'lists.view', 'lists.create', 'lists.edit', 'lists.move_cutline', 'lists.publish',
    'criteria.view', 'criteria.create', 'criteria.edit', 'criteria.delete',
    'criteria.select', 'criteria.weight', 'criteria.lock', 'criteria.reopen',
    'values.propose', 'values.accept', 'values.reject', 'values.override', 'ranking.override',
    'values.define_range', 'values.approve_range', 'values.define_scales', 'values.converge',
    'attributes.define', 'attributes.assign_owner', 'type_lists.edit',
    'catchball.request', 'catchball.propose', 'catchball.return',
    'catchball.accept_provisional', 'catchball.accept', 'catchball.reject',
    'catchball.rollout', 'catchball.aggregate', 'catchball.adjust_path',
    'analytics.view', 'analytics.export',
    'task.assign', 'task.self_assign',
    'admin.users', 'admin.groups', 'admin.roles', 'admin.settings',
    'admin.grant_workspace',
  ],

  // workspace-admin inherits ALL_PERMISSION_ACTIONS minus elevation-granting actions.
  // New permissions added to ALL_PERMISSION_ACTIONS are automatically included here
  // unless explicitly added to WORKSPACE_ADMIN_EXCLUDED_PERMISSIONS.
  'workspace-admin': ALL_PERMISSION_ACTIONS.filter(
    (p) => !WORKSPACE_ADMIN_EXCLUDED_PERMISSIONS.includes(p)
  ),
};
